import React, { createContext, useReducer, useContext, useEffect } from 'react';
import { FeedbackWidget } from './components/FeedbackWidget';
import { useDevModeShortcut } from './hooks/useDevModeShortcut';
import { useDevSkip } from './hooks/useDevSkip';
import { isRuntimeDevMode, DEV_SHORTCUT_ALLOWED } from './config/devFlags';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';

import type {
  Candidate,
  ParsedResume,
  InterviewSession,
  CompositeScore,
  CandidateReadiness,
} from './types';

// ─── Lazy pages ───────────────────────────────────────────────────────────────
const Welcome = React.lazy(() => import('./pages/Welcome').then(m => ({ default: m.Welcome })));
const InviteRedirect = React.lazy(() => import('./pages/InviteRedirect').then(m => ({ default: m.InviteRedirect })));
const CreateProfile = React.lazy(() => import('./pages/CreateProfile').then(m => ({ default: m.CreateProfile })));
const IdentityVerify = React.lazy(() => import('./pages/IdentityVerify').then(m => ({ default: m.IdentityVerify })));
const FetchingResume = React.lazy(() => import('./pages/FetchingResume').then(m => ({ default: m.FetchingResume })));
const UploadResume = React.lazy(() => import('./pages/UploadResume').then(m => ({ default: m.UploadResume })));
const ResumeReview = React.lazy(() => import('./pages/ResumeReview').then(m => ({ default: m.ResumeReview })));
const MeetConnor = React.lazy(() => import('./pages/MeetConnor').then(m => ({ default: m.MeetConnor })));
const InterviewPrep = React.lazy(() => import('./pages/InterviewPrep').then(m => ({ default: m.InterviewPrep })));
const BehavioralInterview = React.lazy(() => import('./pages/BehavioralInterview').then(m => ({ default: m.BehavioralInterview })));
const InterviewComplete = React.lazy(() => import('./pages/InterviewComplete').then(m => ({ default: m.InterviewComplete })));
const CaseSimPrep = React.lazy(() => import('./pages/CaseSimPrep').then(m => ({ default: m.CaseSimPrep })));
const CaseSimulation = React.lazy(() => import('./pages/CaseSimulation').then(m => ({ default: m.CaseSimulation })));
const Writing = React.lazy(() => import('./pages/Writing').then(m => ({ default: m.Writing })));
const SearchResources = React.lazy(() => import('./pages/SearchResources').then(m => ({ default: m.SearchResources })));
const ScheduleMargaret = React.lazy(() => import('./pages/ScheduleMargaret').then(m => ({ default: m.ScheduleMargaret })));
const Results = React.lazy(() => import('./pages/Results').then(m => ({ default: m.Results })));
const InvestorDemo = React.lazy(() => import('./pages/InvestorDemo').then(m => ({ default: m.InvestorDemo })));
const AdminLogin = React.lazy(() => import('./pages/admin/AdminLogin').then(m => ({ default: m.AdminLogin })));
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const AdminCandidateDetail = React.lazy(() => import('./pages/admin/AdminCandidateDetail').then(m => ({ default: m.AdminCandidateDetail })));
const AdminSupportActions = React.lazy(() => import('./pages/admin/AdminSupportActions').then(m => ({ default: m.AdminSupportActions })));
const AdminTuning = React.lazy(() => import('./pages/admin/AdminTuning').then(m => ({ default: m.AdminTuning })));
const AdminFlags = React.lazy(() => import('./pages/admin/AdminFlags').then(m => ({ default: m.AdminFlags })));

// ─── App State ────────────────────────────────────────────────────────────────

interface AppState {
  candidate:         Candidate | null;
  parsedResume:      ParsedResume | null;
  screeningPassed:   boolean | null;
  screeningReason:   string | null;
  identityStatus:    string | null;
  interviewSession:  InterviewSession | null;
  liveKitToken:      string | null;
  liveKitUrl:        string | null;
  stage1Score:       CompositeScore | null;
  stage2Score:       CompositeScore | null;
  overallStatus:     CandidateReadiness['overall_status'] | null;
}

type Action =
  | { type: 'SET_CANDIDATE';         payload: Candidate }
  | { type: 'SET_PARSED_RESUME';     payload: ParsedResume }
  | { type: 'SET_SCREENING';         payload: { passed: boolean; reason: string | null } }
  | { type: 'SET_IDENTITY_STATUS';   payload: string | null }
  | { type: 'SET_INTERVIEW_SESSION'; payload: InterviewSession }
  | { type: 'SET_LIVEKIT';           payload: { token: string; url: string } }
  | { type: 'SET_STAGE1_SCORE';      payload: CompositeScore }
  | { type: 'SET_STAGE2_SCORE';      payload: CompositeScore }
  | { type: 'SET_OVERALL_STATUS';    payload: CandidateReadiness['overall_status'] }
  | { type: 'RESET' };

const initialState: AppState = {
  candidate:        null,
  parsedResume:     null,
  screeningPassed:  null,
  screeningReason:  null,
  identityStatus:   null,
  interviewSession: null,
  liveKitToken:     null,
  liveKitUrl:       null,
  stage1Score:      null,
  stage2Score:      null,
  overallStatus:    null,
};

function appReducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case 'SET_CANDIDATE':
      return { ...(state.candidate?.id === action.payload.id ? state : initialState), candidate: action.payload };
    case 'SET_PARSED_RESUME':
      return { ...state, parsedResume: action.payload };
    case 'SET_SCREENING':
      return { ...state, screeningPassed: action.payload.passed, screeningReason: action.payload.reason };
    case 'SET_IDENTITY_STATUS':
      return { ...state, identityStatus: action.payload };
    case 'SET_INTERVIEW_SESSION':
      return { ...state, interviewSession: action.payload };
    case 'SET_LIVEKIT':
      return { ...state, liveKitToken: action.payload.token, liveKitUrl: action.payload.url };
    case 'SET_STAGE1_SCORE':
      return { ...state, stage1Score: action.payload };
    case 'SET_STAGE2_SCORE':
      return { ...state, stage2Score: action.payload };
    case 'SET_OVERALL_STATUS':
      return { ...state, overallStatus: action.payload };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

// ─── Context ──────────────────────────────────────────────────────────────────

interface AppContextValue {
  state: AppState;
  dispatch: React.Dispatch<Action>;
}

export const AppContext = createContext<AppContextValue>({
  state: initialState,
  dispatch: () => undefined,
});

export function useAppContext(): AppContextValue {
  return useContext(AppContext);
}

// ─── App ──────────────────────────────────────────────────────────────────────

/**
 * Post-voice stage-2 routes. These are "definitive" pipeline states: once the
 * case-sim voice portion hands off, the backend `_compute_pipeline` returns
 * them directly and skips persisted-`current_step` restoration.
 */
const HANDOFF_ROUTE_PREFIXES = ['/search-resources/', '/writing/', '/schedule-margaret/'];

/**
 * Session restore + routing.
 *
 * Lives *inside* BrowserRouter so it can use useNavigate/useLocation.
 * On mount it checks sessionStorage for a stored session, re-hydrates the
 * candidate and any active/paused interview from the backend, then redirects
 * the user to the correct pipeline step if they landed on "/".
 */
function AppRoutes() {
  const { state, dispatch } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();

  useDevModeShortcut();
  const demoSkip = useDevSkip();

  useEffect(() => {
    if (location.pathname.startsWith('/admin')) return;
    // /demo mints its own fresh candidate — don't restore a previous session
    // over it (the restore could race the demo setup and win).
    if (location.pathname === '/demo') return;

    const storedToken = sessionStorage.getItem('baba_session_token');
    const storedCandidateId = sessionStorage.getItem('baba_candidate_id');
    if (!storedToken || !storedCandidateId || state.candidate) return;

    (async () => {
      try {
        const { getCandidate, getPipelineState, getInterviewStatus } = await import('./api/client');

        const candidate = await getCandidate(storedCandidateId);
        dispatch({ type: 'SET_CANDIDATE', payload: candidate });

        try {
          const pipeline = await getPipelineState(storedCandidateId);
          dispatch({ type: 'SET_OVERALL_STATUS', payload: pipeline.overall_status });

          // Restore the active/paused interview session into React state so
          // the Resume button on prep pages is visible without an extra click.
          // Prefer stage 2: once a stage-2 interview exists it is the candidate's
          // live session, and hydrating the stage-1 row instead makes
          // isWritingHandoffPending() (which requires stage === 2) return false,
          // hiding the post-voice writing/tech-literacy resume affordances.
          const activeInterviewId = pipeline.stage2_interview_id ?? pipeline.stage1_interview_id;
          if (activeInterviewId) {
            try {
              const session = await getInterviewStatus(activeInterviewId);
              dispatch({ type: 'SET_INTERVIEW_SESSION', payload: session });
            } catch {
              // Non-fatal — prep pages will still load; user may need to start fresh.
            }
          }

          // Only redirect if the user landed on the root path (i.e., they
          // typed/clicked the bare app URL or closed the tab and came back).
          // If they deep-linked to a specific page, honour that URL instead.
          //
          // Exception: the post-voice stage-2 chain (/search-resources,
          // /writing, /schedule-margaret) is a definitive state — the backend
          // pipeline short-circuits current_step restoration for it entirely.
          // A candidate who lands on an earlier static page (e.g. back-buttons
          // to /interview-prep after the case sim handed off to the on-screen
          // tasks) gets no affordance to move forward again, and useTrackStep
          // on that page rewrites their persisted step backwards. Pull them
          // back onto the chain instead of stranding them.
          const inHandoffChain = (path: string) =>
            HANDOFF_ROUTE_PREFIXES.some(prefix => path.startsWith(prefix));
          if (
            location.pathname === '/' ||
            (inHandoffChain(pipeline.next_step) && !inHandoffChain(location.pathname))
          ) {
            navigate(pipeline.next_step, { replace: true });
          }
        } catch {
          // Pipeline fetch failed — candidate is restored; they'll land wherever
          // their URL points. Non-fatal.
        }
      } catch {
        // Token expired or invalid — clear storage so they start fresh.
        sessionStorage.removeItem('baba_session_token');
        sessionStorage.removeItem('baba_candidate_id');
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <React.Suspense fallback={<RouteLoading />}>
      <Routes>
      {/* Welcome / landing */}
      <Route path="/"                      element={<Welcome />} />
      {/* In production /i/:code never reaches the app: vercel.json redirects it to
          new Robin, which sends candidates who already started back to /continue/:code. */}
      <Route path="/i/:code"               element={<InviteRedirect />} />
      <Route path="/continue/:code"        element={<InviteRedirect />} />

      {/* Step 1: Meet Connor (video intro) */}
      <Route path="/meet-connor"           element={<MeetConnor />} />

      {/* Step 2: Profile creation */}
      <Route path="/create-profile"        element={<CreateProfile />} />

      {/* Step 3: Identity verification */}
      <Route path="/identity-verify"       element={<IdentityVerify />} />

      {/* Step 4: Resume fetch + review (upload kept as fallback) */}
      <Route path="/fetching-resume"       element={<FetchingResume />} />
      <Route path="/upload-resume"         element={<UploadResume />} />
      <Route path="/resume-review"         element={<ResumeReview />} />

      {/* Step 5: Behavioral interview */}
      <Route path="/interview-prep"        element={<InterviewPrep />} />
      <Route path="/behavioral-interview"  element={<BehavioralInterview />} />
      <Route path="/interview-complete"    element={<InterviewComplete />} />

      {/* Step 5: Case simulation */}
      <Route path="/case-sim-prep"         element={<CaseSimPrep />} />
      <Route path="/case-simulation"       element={<CaseSimulation />} />

      {/* Step 5a: Resource lookup, runs before email */}
      <Route path="/search-resources/:interviewId" element={<SearchResources />} />

      {/* Step 5b: Writing exercise (separately resumable via magic link) */}
      <Route path="/writing/:interviewId"  element={<Writing />} />

      {/* Step 5c: Calendar scheduling, runs after email */}
      <Route path="/schedule-margaret/:interviewId" element={<ScheduleMargaret />} />

      {/* Step 6: Results */}
      <Route path="/results"               element={<Results />} />

      {/* Investor demo — fresh pre-gated candidate straight to case-sim prep */}
      <Route path="/demo"                  element={<InvestorDemo />} />

      {/* Admin dashboard */}
      <Route path="/admin/login"           element={<AdminLogin />} />
      <Route path="/admin"                 element={<AdminDashboard />} />
      <Route path="/admin/candidates/:candidateId" element={<AdminCandidateDetail />} />
      <Route path="/admin/support"             element={<AdminSupportActions />} />
      <Route path="/admin/tuning"              element={<AdminTuning />} />
      <Route path="/admin/flags"               element={<AdminFlags />} />

      {/* Fallback */}
      <Route path="*"                      element={<Navigate to="/" replace />} />
      </Routes>
      {demoSkip.showDemoSkip && (
        <button
          type="button"
          onClick={demoSkip.skip}
          className="fixed top-4 right-4 z-[80] rounded-full border border-border bg-canvas/90 px-3 py-1.5 text-xs text-muted shadow-sm backdrop-blur-sm hover:text-ink"
        >
          Skip step →
        </button>
      )}
    </React.Suspense>
  );
}

function RouteLoading() {
  return (
    <div className="min-h-[100dvh] bg-canvas flex items-center justify-center px-6">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-surface p-5">
        <div className="h-2 w-24 rounded-full bg-sage/30 animate-pulse mb-4" />
        <div className="space-y-2">
          <div className="h-3 rounded-full bg-slate-200 animate-pulse" />
          <div className="h-3 w-2/3 rounded-full bg-slate-200 animate-pulse" />
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [state, dispatch] = useReducer(appReducer, initialState);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {DEV_SHORTCUT_ALLOWED && isRuntimeDevMode() && (
        <div className="fixed top-0 inset-x-0 z-[9999] flex items-center justify-center gap-2 bg-amber-400/90 backdrop-blur-sm py-1.5 text-[11px] font-mono font-bold tracking-widest text-amber-950">
          <span>⚠ DEV MODE ACTIVE</span>
          <span className="opacity-50">·</span>
          <span className="font-normal opacity-70">⌘⌥⇧D to disable</span>
        </div>
      )}
      <BrowserRouter>
        <AppRoutes />
        <FeedbackWidget />
      </BrowserRouter>
    </AppContext.Provider>
  );
}
